import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  finalize,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  tap,
} from 'rxjs';
import {
  CuratorUser,
  ICommonUser,
  ITeacherUser,
  MethodistUser,
  StudentUser,
} from '../../model/interface/users';
import {
  SelectValue,
  SortableResponseData,
} from '../../model/interface/helper';
import * as Sentry from '@sentry/angular';

@Injectable({
  providedIn: 'root',
})
export class UsersService {
  private readonly userProfileSubject$ = new BehaviorSubject<
    (StudentUser & MethodistUser & CuratorUser) | null
  >(null);

  public readonly userProfile$ = this.userProfileSubject$.pipe(
    switchMap(profile => {
      if (!profile) {
        return this.getUserProfile();
      }

      return of(profile);
    }),
    shareReplay({ bufferSize: 1, refCount: false })
  );

  private readonly teachersList$ = new BehaviorSubject<SelectValue[]>([]);

  private readonly teachersListLoading$ = new BehaviorSubject<boolean>(false);

  constructor(private httpClient: HttpClient) {}

  public getUserProfile(): Observable<
    StudentUser & MethodistUser & CuratorUser
  > {
    return this.httpClient
      .get<StudentUser & MethodistUser & CuratorUser>(`/v1/users/profile`)
      .pipe(
        tap((res: StudentUser & MethodistUser & CuratorUser) => {
          if (res.instagramLink) {
            res.instagramLink = res.instagramLink.replace(
              'https://www.instagram.com/',
              ''
            );
          }

          if (res?.id) {
            Sentry.setUser({
              id: res.id,
              username: res.username,
            });
            Sentry.setTag('userId', res.id);
          }
          this.userProfileSubject$.next(res);
        }),
        shareReplay({
          bufferSize: 1,
          refCount: true,
        })
      );
  }

  public resetUserProfile(
    profileData: (StudentUser & MethodistUser & CuratorUser) | null = null
  ): void {
    this.userProfileSubject$.next(profileData);
  }

  public updateUserProfile(
    data: StudentUser | MethodistUser | CuratorUser
  ): Observable<StudentUser | MethodistUser | CuratorUser> {
    return this.httpClient
      .put<StudentUser | MethodistUser | CuratorUser>(`/v1/users/profile`, data)
      .pipe(
        map((res: StudentUser | MethodistUser | CuratorUser) => {
          this.userProfileSubject$.next(
            res as StudentUser & MethodistUser & CuratorUser
          );
          return res;
        })
      );
  }

  getTeachersList(): Observable<SelectValue[]> {
    if (this.teachersList$.value.length || this.teachersListLoading$.value) {
      return this.teachersList$.asObservable();
    }

    this.teachersListLoading$.next(true);

    return this.httpClient.get<StudentUser>('/v1/users/teachers').pipe(
      map((res: StudentUser) => {
        const selectValues: SelectValue[] = res.map((user: StudentUser) => ({
          name: `${user.firstname} ${user.lastname}`,
          value: user.id || '',
        }));

        return selectValues;
      }),
      tap(res => this.teachersList$.next(res)),
      finalize(() => this.teachersListLoading$.next(false))
    );
  }

  getTeachersListV2$(
    pageIndex: number = 0,
    searchWord: string = '',
    pageSize: number = 10
  ): Observable<SortableResponseData<ITeacherUser>> {
    const queryString = `size=${pageSize}&page=${pageIndex}&search=${searchWord}`;

    return this.httpClient.get<SortableResponseData<ITeacherUser>>(
      `/v2/users/teachers?${queryString}`
    );
  }

  public getUsers({
    page,
    size,
    roles,
    search,
  }: {
    page: number;
    size: number;
    roles: string[];
    search?: string;
  }): Observable<SortableResponseData<any>> {
    let params: any = { page, size, roles };
    if (search) params.search = search;
    return this.httpClient.get<SortableResponseData<any>>(`/v1/users`, {
      params,
    });
  }

  public inviteUser({
    email,
    roles,
    lastName,
    firstName,
    phone,
    subjects,
  }: {
    email: string;
    roles: string[];
    lastName?: string;
    firstName?: string;
    phone?: string | null;
    subjects?: string[];
  }): Observable<any> {
    const params: any = {
      email,
      roles: [...roles],
      phoneNumber: phone,
    };

    if (lastName) params.lastname = lastName;
    if (firstName) params.firstname = firstName;
    if (subjects) params.subjects = subjects;
    return this.httpClient.post(`/v1/users/sendregistrationlink/new`, params);
  }

  public saveUser(data: any): Observable<any> {
    return this.httpClient.post(`/v1/users`, data);
  }

  public getAllUsersByRole(
    roles: string[],
    search: string = ''
  ): Observable<ICommonUser[]> {
    return this.httpClient.get<ICommonUser[]>(`/v2/users`, {
      params: {
        roles,
        search,
      },
    });
  }

  public getTeachers(): Observable<any[]> {
    return this.httpClient.get<any[]>('/v1/users/teachers');
  }

  public getUserById(userId: string): Observable<any> {
    return this.httpClient.get<any>(`/v1/users/${userId}`);
  }

  public checkUserRegistered(username: string): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `/v1/users/username/${username}/registered`
    );
  }

  public checkNumberExist(phone: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`/v1/users/phonenumber/${phone}/exist`);
  }

  public checkUsernameExist(username: string): Observable<boolean> {
    return this.httpClient.get<boolean>(`/v1/users/username/${username}/exist`);
  }

  public getStudents(): Observable<StudentUser[]> {
    return this.httpClient.get<StudentUser[]>(`/v1/users/students`);
  }

  public getMetaInfo(username: string): Observable<any> {
    return this.httpClient.get<any>(`/v1/users/${username}/metainfo`);
  }

  public sendRegistrationLink(userId: string): Observable<boolean> {
    return this.httpClient.post<boolean>(
      `/v1/users/${userId}/sendregistrationlink`,
      {}
    );
  }
}
